import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiCheck, FiPlus, FiTrash } from 'react-icons/all';
import {
  FiChevronLeft,
  FiDownload,
  FiEdit2,
  FiEdit3,
  FiX,
} from 'react-icons/fi';
import { Input, Table } from '~/components';
import { useDocumentsTypes } from '~/hooks/useDocuments/useDocuments';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { SelectItem } from '~/types';

type FormData = {
  nm_tipo: string;
};

export const TypeForm: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isEdit, setEdit] = useState<boolean[]>([]);
  const [tipo, setTipo] = useState<string>('');
  const [tipoEdit, setTipoEdit] = useState<SelectItem[]>([]);
  const [isFetching, setFetching] = useState(false);
  const toast = useToast();
  const {
    data: types,
    isLoading: isLoadingTypes,
    refetch,
  } = useDocumentsTypes();

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearErrors();
    onClose();
  };

  async function handleCreate(formData: FormData) {
    clearErrors();

    (!tipo || !tipo?.trim().length) &&
      setError('nm_tipo', {
        type: 'required',
        message: 'Escreva o nome do tipo',
      });
    tipo.length > 60 &&
      setError('nm_tipo', {
        type: 'maxLength',
        message: 'Nome do tipo com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { nome: tipo?.trim(), ativo: true };

    setFetching(true);

    api
      .post(`documento_tipo`, form)
      .then(() => {
        toast({
          title: `Tipo adicionado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setTipo('');
        setValue('nm_tipo', '');
        queryClient.invalidateQueries('documentsType');
      })
      .catch(error => {
        if (error.response.status === 400) {
          const errorsValidate = Object.entries(error.response.data);
          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao criar tipo`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao adicionar tipo`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  async function handleSubmitEdit(index: number) {
    clearErrors();

    (!tipoEdit[index] || !tipoEdit[index].label.trim().length) &&
      setError(`type-${index}`, {
        type: 'required',
        message: 'Escreva o nome do tipo',
      });

    tipoEdit[index].label.length > 60 &&
      setError(`type-${index}`, {
        type: 'maxLength',
        message: 'Nome do tipo com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { nome: tipoEdit[index].label.trim(), ativo: true };

    setFetching(true);

    api
      .put(`documento_tipo/${tipoEdit[index]?.value}`, form)
      .then(() => {
        toast({
          title: `Tipo editado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setTipoEdit([...tipoEdit]);
        handleEdit(index);
        queryClient.invalidateQueries('documentsType');
      })
      .catch(error => {
        toast({
          title: `Erro ao editar tipo`,
          description: `${
            tipoEdit[index].label.length > 60
              ? 'Nome do tipo com mais de 60 caracteres'
              : !tipoEdit[index].label.trim().length
              ? 'Nome do tipo não pode ser vazio'
              : 'Nome do tipo não pode ser repetido'
          }`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleEdit = (i: number, value?: SelectItem) => {
    clearErrors();
    isEdit[i] = !isEdit[i];
    setEdit([...isEdit]);
    if (value) {
      tipoEdit[i] = value;
      setTipoEdit([...tipoEdit]);
    }
  };

  const canEdit = (index: number) => {
    return isEdit[index];
  };

  const handleTipo = (event: any) => {
    setTipo(event.target.value);
    clearErrors();
  };

  const handleTipoEdit = (event: any, index: number, id: number) => {
    tipoEdit[index] = { value: id.toString(), label: event.target.value };
    clearErrors(`type-${index}`);
  };
  async function handleDelete(id: number) {
    api
      .delete(`documento_tipo/${id}`)
      .then(() => {
        toast({
          title: `Tipo deletado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('documentsType');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `${error.response.data.detail}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  return (
    <>
      <Box
        as="button"
        ref={btnRef}
        onClick={handleOpen}
        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
        border="1px"
        px="8px"
        borderRadius="4px"
        fontSize="sm"
        bg="gray.900"
        borderColor="gray.900"
        color="#fff"
        marginLeft="10px"
        h="40px"
      >
        <Flex align="center" gridGap="2">
          <Text fontSize={{ lg: 'sm', md: 'sm' }}>Novo</Text>
        </Flex>
      </Box>
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton />
          <DrawerHeader>
            <Icon
              as={FiChevronLeft}
              style={{
                cursor: 'pointer',
                fontSize: '28px',
                marginRight: '10px',
              }}
              onClick={handleClose}
            />
            Cadastro de tipo
          </DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={400} w="100%">
              <Stack gridGap={4} />
              <FormControl>
                <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                  Novo tipo
                </FormLabel>
                <InputGroup>
                  <FormControl isInvalid={errors?.nm_tipo}>
                    <Input
                      name="nm_tipo"
                      type="text"
                      error={errors.nm_tipo}
                      fontSize="15px"
                      placeholder="Entre com o novo tipo"
                      onChange={handleTipo}
                      value={tipo}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    color="white"
                    background="gray.900"
                    _hover={{
                      bgColor: 'black',
                    }}
                    style={{ marginLeft: '10px' }}
                    onClick={handleSubmit(handleCreate)}
                  >
                    <Icon as={FiPlus} />
                  </Button>
                </InputGroup>
              </FormControl>
              <Table type="secondary" size="sm">
                <Thead>
                  <Tr>
                    <Th>Tipos cadastrados</Th>
                    <Th style={{ textAlign: 'right', paddingRight: '45px' }}>
                      Ações
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {types &&
                    !isLoadingTypes &&
                    types?.map((t: any, index: number) => (
                      <Tr key={t.value}>
                        {!canEdit(index) ? (
                          <>
                            <Td>
                              <Tooltip label={t.label} placement="top">
                                <Text isTruncated style={{ width: '240px' }}>
                                  {t.label}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td
                              style={{
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Tooltip label="Editar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Edit type"
                                  icon={<Icon as={FiEdit2} />}
                                  onClick={() => handleEdit(index, t)}
                                  marginRight="5px"
                                />
                              </Tooltip>
                              <Tooltip label="Deletar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Remove type"
                                  icon={<Icon as={FiTrash} />}
                                  onClick={() => handleDelete(t.value)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        ) : (
                          <>
                            <Td>
                              <FormControl isInvalid={errors[`type-${index}`]}>
                                <Input
                                  name="type"
                                  type="text"
                                  error={errors[`type-${index}`]}
                                  fontSize="15px"
                                  placeholder="Editar o tipo de cadastro"
                                  size="sm"
                                  defaultValue={tipoEdit[index].label}
                                  onChange={e =>
                                    handleTipoEdit(e, index, t.value)
                                  }
                                />
                              </FormControl>
                            </Td>
                            <Td style={{ textAlign: 'right' }}>
                              <Tooltip label="Confirmar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Confirm"
                                  icon={<Icon as={FiCheck} />}
                                  marginRight="5px"
                                  onClick={() => handleSubmitEdit(index)}
                                />
                              </Tooltip>
                              <Tooltip label="Cancelar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Cancel"
                                  icon={<Icon as={FiX} />}
                                  onClick={() => handleEdit(index)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        )}
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </Stack>
            <Button
              type="submit"
              onClick={handleClose}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px', marginBottom: '20px' }}
            >
              Voltar
            </Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
